// Cross-Check (Claims and Evidence) panel — SPEC §3.9 / Phase 6.
//
// Renders one section per cross-checkable document in the Case, each
// listing claim→verdict pairs with evidence excerpts. Provides
// inline "Run cross-check" + override controls.

import { useState } from "react";

import {
  ApiError,
  crossCheck,
  sortClaimsForDisplay,
} from "../../lib/api";
import type {
  ClaimWithVerdict,
  CrossCheckDocumentSection,
  VerdictKind,
} from "../../lib/types";
import { useAsync } from "../../hooks/useAsync";
import { pushToast } from "../Toast";
import { VerdictBadge } from "../StatusBadge";

interface Props {
  caseId: number;
}

const CATEGORY_LABEL: Record<string, string> = {
  diagnosis: "Diagnosis",
  symptom: "Symptom / finding",
  prior_treatment_tried: "Prior treatment",
  records_reviewed: "Records reviewed",
  criteria_applied: "Criteria / chapter cited",
  other: "Other assertion",
};

export function CaseCrossCheckTab({ caseId }: Props): JSX.Element {
  const { data, loading, error, refresh } = useAsync(
    () => crossCheck.casePanel(caseId),
    [caseId],
  );
  const [runningDocId, setRunningDocId] = useState<number | null>(null);
  const [overrideForVerdict, setOverrideForVerdict] = useState<ClaimWithVerdict | null>(null);

  const onRun = async (sourceDocId: number): Promise<void> => {
    setRunningDocId(sourceDocId);
    try {
      const res = await crossCheck.extractClaims(sourceDocId, caseId);
      pushToast(
        "success",
        `Cross-check ran — ${res.items.length} claim${res.items.length === 1 ? "" : "s"} (${res.extraction_mode})`,
      );
      refresh();
    } catch (err) {
      pushToast(
        "error",
        err instanceof ApiError ? `${err.code}: ${err.message}` : "Cross-check failed",
      );
    } finally {
      setRunningDocId(null);
    }
  };

  return (
    <section>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-slate-800">Claims and Evidence</h2>
          <p className="text-xs text-slate-500">
            Cross-Check extracts factual claims from opposing-party documents (denial letters,
            DD reports, UR decisions, RMEs, peer reviews, adjuster correspondence, carrier
            disputes) and matches each one against your records. Verdicts:
            <span className="ml-1 badge-green">supported</span>{" "}
            <span className="badge-red">contradicted</span>{" "}
            <span className="badge-gray">not addressed</span>.
          </p>
        </div>
        <button type="button" onClick={refresh} className="btn-secondary">
          Refresh
        </button>
      </div>
      {data && (
        <div className="mb-4 flex flex-wrap gap-2 text-xs">
          <span className="badge-blue">{data.total_claims} claims</span>
          <span className="badge-green">{data.total_supported} supported</span>
          <span className="badge-red">{data.total_contradicted} contradicted</span>
          <span className="badge-gray">{data.total_not_addressed} not addressed</span>
        </div>
      )}
      {loading && <p className="text-sm text-slate-500">Loading panel…</p>}
      {error && <p className="text-sm text-red-700">Could not load panel: {error.message}</p>}
      {data && data.sections.length === 0 && (
        <p className="text-sm text-slate-600">
          No cross-checkable documents in this Case yet. Upload a denial letter, DD report,
          UR decision, RME, peer review, adjuster correspondence, or carrier dispute response
          to enable cross-check.
        </p>
      )}
      {data && data.sections.map((section) => (
        <SectionView
          key={section.source_document_id}
          section={section}
          runningDocId={runningDocId}
          onRun={onRun}
          onOverride={(item) => setOverrideForVerdict(item)}
        />
      ))}
      {overrideForVerdict && (
        <OverrideDialog
          item={overrideForVerdict}
          onClose={() => setOverrideForVerdict(null)}
          onSaved={() => {
            setOverrideForVerdict(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function SectionView(props: {
  section: CrossCheckDocumentSection;
  runningDocId: number | null;
  onRun: (sourceDocId: number) => void;
  onOverride: (item: ClaimWithVerdict) => void;
}): JSX.Element {
  const { section, runningDocId, onRun, onOverride } = props;
  const items = sortClaimsForDisplay(section.items);
  return (
    <section className="card mb-4 p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-medium text-slate-900">
            {section.source_filename}
          </h3>
          <p className="text-xs text-slate-500">
            {section.source_doc_type ?? "uncategorized"} · {items.length} claim
            {items.length === 1 ? "" : "s"}
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          disabled={runningDocId === section.source_document_id}
          onClick={() => onRun(section.source_document_id)}
        >
          {runningDocId === section.source_document_id ? "Running…" : items.length === 0 ? "Run cross-check" : "Re-run"}
        </button>
      </div>
      {items.length === 0 && (
        <p className="text-sm text-slate-600">
          No claims extracted yet. Click <span className="font-medium">Run cross-check</span> to
          extract claims from this document and assign verdicts against the rest of the Case.
        </p>
      )}
      {items.length > 0 && (
        <ul role="list" className="divide-y divide-slate-200">
          {items.map((item) => (
            <ClaimRow key={item.claim.id} item={item} onOverride={onOverride} />
          ))}
        </ul>
      )}
    </section>
  );
}

function ClaimRow({
  item,
  onOverride,
}: {
  item: ClaimWithVerdict;
  onOverride: (item: ClaimWithVerdict) => void;
}): JSX.Element {
  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            {CATEGORY_LABEL[item.claim.claim_category] ?? item.claim.claim_category}
          </div>
          <blockquote className="mt-1 max-w-prose-narrow border-l-2 border-slate-300 pl-3 text-sm italic text-slate-800">
            “{item.claim.claim_text}”
          </blockquote>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <VerdictBadge verdict={item.verdict.effective_verdict} />
          {item.verdict.user_override && (
            <span className="text-xs text-slate-500">user override</span>
          )}
        </div>
      </div>
      {item.verdict.evidence_excerpts.length > 0 && (
        <div className="mt-2 space-y-1 text-xs">
          {item.verdict.evidence_excerpts.map((e) => (
            <div
              key={`${e.document_id}-${e.chunk_index}`}
              className={`rounded-md border px-2 py-1 ${
                e.polarity === "contradicts"
                  ? "border-red-200 bg-red-50 text-red-900"
                  : "border-emerald-200 bg-emerald-50 text-emerald-900"
              }`}
            >
              <span className="font-medium">{e.polarity === "contradicts" ? "Contradicts" : "Supports"}</span>{" "}
              <span className="text-slate-500">(doc #{e.document_id}, chunk {e.chunk_index}, score {e.score.toFixed(2)})</span>
              <p className="mt-0.5 text-slate-800">{e.text.slice(0, 240)}{e.text.length > 240 ? "…" : ""}</p>
            </div>
          ))}
        </div>
      )}
      {item.verdict.reasoning && (
        <p className="mt-1 text-xs text-slate-500">{item.verdict.reasoning}</p>
      )}
      <div className="mt-2 flex items-center gap-3 text-xs">
        {item.verdict.id > 0 && (
          <button
            type="button"
            className="text-brand-700 underline hover:text-brand-900"
            onClick={() => onOverride(item)}
          >
            Override verdict
          </button>
        )}
        <span className="text-slate-400">
          conf {item.verdict.confidence.toFixed(2)} · claim #{item.claim.id} · verdict #{item.verdict.id}
        </span>
      </div>
    </li>
  );
}

function OverrideDialog({
  item,
  onClose,
  onSaved,
}: {
  item: ClaimWithVerdict;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [verdict, setVerdict] = useState<VerdictKind>(
    item.verdict.user_override ?? item.verdict.verdict,
  );
  const [reason, setReason] = useState(item.verdict.override_reason ?? "");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    try {
      await crossCheck.override(item.verdict.id, verdict, reason);
      pushToast("success", `Verdict #${item.verdict.id} overridden`);
      onSaved();
    } catch (err) {
      pushToast(
        "error",
        err instanceof ApiError ? err.message : "Failed to record override",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <form onSubmit={onSubmit} className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-medium text-slate-900">Override verdict</h3>
        <p className="mb-4 text-xs text-slate-500">
          The system verdict stays on the row; your override surfaces as the{" "}
          <em>effective verdict</em> in subsequent reads. This action is audited.
        </p>
        <blockquote className="mb-4 border-l-2 border-slate-300 pl-3 text-sm italic text-slate-700">
          “{item.claim.claim_text}”
        </blockquote>
        <div className="mb-4 space-y-2">
          {(["supported", "contradicted", "not_addressed"] as const).map((v) => (
            <label key={v} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="verdict"
                value={v}
                checked={verdict === v}
                onChange={() => setVerdict(v)}
                className="h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="capitalize">{v.replace("_", " ")}</span>
            </label>
          ))}
        </div>
        <label htmlFor="override-reason" className="label">
          Reason
        </label>
        <textarea
          id="override-reason"
          required
          rows={3}
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you overriding the system verdict?"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving || !reason.trim()}>
            {saving ? "Saving…" : "Save override"}
          </button>
        </div>
      </form>
    </div>
  );
}
